import { faEdit, faLockHashtag, faCheckSquare, faXmarkSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CellContext } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { User } from 'src/features/auth/types';
import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import DeactivateUserFormModal from 'src/features/users/components/forms/DeactivateUserFormModal';
import { userMessages } from 'src/features/users/utils/messages';
import ActivateUserFormModal from 'src/features/users/components/forms/ActivateUserFormModal';
import UpdateUserFormModal from 'src/features/users/components/forms/UpdateUserFormModal';
import ChangeUserPasswordFormModal from 'src/features/users/components/forms/ChangeUserPasswordFormModal';

export default function CellUserActions({ row }: CellContext<User, unknown>) {
    const { formatMessage } = useIntl();

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                {row.original.is_active ? (
                    <Tooltip>
                        <DeactivateUserFormModal
                            selectedUser={row.original}
                            semantics={{ entryName: formatMessage(userMessages.user).toLowerCase() }}
                        >
                            <TooltipTrigger asChild>
                                <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                    <FontAwesomeIcon icon={faXmarkSquare} />
                                </Button>
                            </TooltipTrigger>
                        </DeactivateUserFormModal>
                        <TooltipContent>
                            <FormattedMessage
                                defaultMessage={userMessages.deactivateUser.defaultMessage}
                                id={userMessages.deactivateUser.id}
                            />
                        </TooltipContent>
                    </Tooltip>
                ) : (
                    <Tooltip>
                        <ActivateUserFormModal
                            selectedUser={row.original}
                            semantics={{ entryName: formatMessage(userMessages.user).toLowerCase() }}
                        >
                            <TooltipTrigger asChild>
                                <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                    <FontAwesomeIcon icon={faCheckSquare} />
                                </Button>
                            </TooltipTrigger>
                        </ActivateUserFormModal>
                        <TooltipContent>
                            <FormattedMessage
                                defaultMessage={userMessages.activateUser.defaultMessage}
                                id={userMessages.activateUser.id}
                            />
                        </TooltipContent>
                    </Tooltip>
                )}
                <Tooltip>
                    <UpdateUserFormModal
                        semantics={{ entryName: formatMessage(userMessages.user).toLowerCase() }}
                        selectedUser={row.original}
                    >
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={faEdit} />
                            </Button>
                        </TooltipTrigger>
                    </UpdateUserFormModal>
                    <TooltipContent>
                        <FormattedMessage
                            defaultMessage={generalMessages.editEntry.defaultMessage}
                            id={generalMessages.editEntry.id}
                            values={{ entry: formatMessage(userMessages.user).toLowerCase() }}
                        />
                    </TooltipContent>
                </Tooltip>
                <Tooltip>
                    <ChangeUserPasswordFormModal
                        selectedUser={row.original}
                        semantics={{ entryName: formatMessage(userMessages.user).toLowerCase() }}
                    >
                        <TooltipTrigger asChild>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={faLockHashtag} />
                            </Button>
                        </TooltipTrigger>
                    </ChangeUserPasswordFormModal>
                    <TooltipContent>
                        <FormattedMessage
                            defaultMessage={userMessages.changePassword.defaultMessage}
                            id={userMessages.changePassword.id}
                        />
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}
